import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import useGlobalStore from '@/stores/global.store';
import { OrderValidationStatus } from '@/types/api/order/order-validation-status.enum';
import useModalStore from '@/stores/modal.store';
import { ModalType } from '@/types/stores/modal';
import { usePollOrderQuery } from './queries/use-poll-order-query';
import { useTimeoutWithControl } from './use-timeout-with-control';
import config from '@/config/index';
import { useCancelOrder } from './use-cancel-order';
import { useCode } from './use-code';

export default function usePollOrderValidation() {
  const [polling, setPolling] = React.useState<boolean>(false);
  const validationStatusRef = React.useRef<OrderValidationStatus | undefined>(undefined);

  const openModal = useModalStore((state) => state.open);
  const closeModal = useModalStore((state) => state.close);
  const orderId = useGlobalStore((state) => state.orderId);
  const browse = useGlobalStore((state) => state.browse);
  const code = useCode();
  const navigate = useNavigate();

  const { cancel: cancelOrder } = useCancelOrder();
  const { data: order } = usePollOrderQuery(orderId!, polling, config.app.orderValidation.pollInterval);
  const { start: startTimout, abort: abortTimeout } = useTimeoutWithControl(() => {
    setPolling(false);
    browse();
    navigate(`/${code}/products`, { replace: true });
    openModal(ModalType.OrderValidationFailed, { message: 'Valideringen tog för lång tid.' });
  }, config.app.orderValidation.timout);

  React.useEffect(() => {
    if (!order) return;

    validationStatusRef.current = order.validationStatus;

    switch (order.validationStatus) {
      case OrderValidationStatus.Pending:
        startTimout();
        setPolling(true);
        openModal(ModalType.OrderValidating, {
          onClose: () => {
            if (validationStatusRef.current === OrderValidationStatus.Pending) cancelOrder();
          },
        });
        break;

      case OrderValidationStatus.Failed:
        setPolling(false);
        abortTimeout();
        browse();
        navigate(`/${code}/products`, { replace: true });
        openModal(ModalType.OrderValidationFailed, { message: 'Valideringen misslyckades.' });
        break;

      case OrderValidationStatus.Validated:
        setPolling(false);
        abortTimeout();
        closeModal();
        break;
    }

    return () => {
      abortTimeout();
      setPolling(false);
    };
  }, [order?.validationStatus, openModal, setPolling, browse]);
}
